import React, { forwardRef } from 'react';
import DOMPurify from 'dompurify';
import Icon from './Icon';

/**
 * EGGS Checkbox Component
 * Based on EGGS Design System specifications
 * 
 * @param {string} size - lg | md | sm
 * @param {string} label - Checkbox label
 * @param {string} helperText - Helper text below checkbox
 * @param {string} errorText - Error message (overrides helperText)
 * @param {boolean} required - Required field indicator
 * @param {boolean} disabled - Disabled state
 * @param {boolean} indeterminate - Indeterminate state
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional checkbox props
 */
const Checkbox = forwardRef(({
  size = 'md',
  label,
  helperText,
  errorText,
  required = false,
  disabled = false,
  indeterminate = false,
  className = '',
  checked,
  onChange,
  ...props
}, ref) => {
  const isError = errorText;

  // Base classes for checkbox container
  const containerClasses = [
    'relative',
    'flex',
    'items-start',
    'gap-3'
  ].join(' ');

  // Checkbox wrapper classes
  const checkboxWrapperClasses = [
    'relative',
    'flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    'border-2',
    'rounded',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'cursor-pointer',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    checked || indeterminate ? 'bg-eggs-yellow border-eggs-yellow' : 'bg-white border-gray-300',
    !disabled && !checked && !indeterminate ? 'hover:border-eggs-yellow' : '',
    isError ? 'border-error' : ''
  ].join(' ');

  // Size-specific classes
  const sizeClasses = {
    lg: {
      checkbox: 'w-6 h-6',
      icon: 16,
      label: 'text-lg',
      helper: 'text-sm'
    },
    md: {
      checkbox: 'w-5 h-5',
      icon: 14,
      label: 'text-base',
      helper: 'text-sm'
    },
    sm: {
      checkbox: 'w-4 h-4',
      icon: 12,
      label: 'text-sm',
      helper: 'text-xs'
    }
  };

  // Label classes
  const labelClasses = [
    'font-burns',
    'font-extralight',
    'text-eggs-dark',
    'cursor-pointer',
    'select-none',
    sizeClasses[size].label,
    disabled ? 'opacity-50 cursor-not-allowed' : ''
  ].join(' ');

  // Helper/error text classes
  const helperTextClasses = [
    'mt-1',
    'font-burns',
    'font-extralight',
    sizeClasses[size].helper,
    isError ? 'text-error' : 'text-gray-500'
  ].join(' ');

  const displayText = errorText || helperText;

  return (
    <div className={`${containerClasses} ${className}`}>
      <label className="flex items-start gap-3 cursor-pointer min-h-[44px] py-2">
        <span
          className={`${checkboxWrapperClasses} ${sizeClasses[size].checkbox}`}
        >
          <input
            ref={ref}
            type="checkbox"
            className="sr-only"
            disabled={disabled}
            checked={checked}
            onChange={(e) => onChange && onChange(e.target.checked)}
            {...props}
          />
          
          {(checked || indeterminate) && (
            <Icon 
              name={indeterminate ? 'minus' : 'check'} 
              size={sizeClasses[size].icon}
              className="text-eggs-dark"
            />
          )}
        </span>
        
        <span className="flex-1">
          {label && (
            <span
              className={labelClasses}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label) + (required ? '<span class="text-error ml-1">*</span>' : '') }}
            />
          )}
          
          {displayText && (
            <p className={helperTextClasses}>
              {displayText}
            </p>
          )}
        </span>
      </label>
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;